import { getInhibitorType, getInhibitorTypeId } from 'src/types/ViewModel/CargoTempLog/InhibitorType';
import { CargoTemperatureConfigBM } from '../../types/BusinessModel/CargoTempLog/CargoTemperatureConfigBM';
import { CargoTemperatureConfig } from '../../types/ViewModel/CargoTempLog/CargoTemperatureConfig';
import { mapCargoTemperatureInputFieldBMs, mapCargoTemperatureInputFields } from './CargoTempLogInputFieldFactory';
import { formatDateTime } from 'odfjell-date-time-utils/dateTimeFormatter';
import { dedupeLogTimes } from '../../components/cargo-temp-log/CargoTemperatureLogUtils';

export const mapCargoTemperatureConfig = (bm: CargoTemperatureConfigBM): CargoTemperatureConfig => {
    return {
        cargoTemperatureConfigId: bm.cargoTemperatureConfigId,
        imoNumber: bm.imoNumber,
        cargoNumber: bm.cargoNumber,
        tradeName: bm.tradeName,
        stowage: bm.stowage,
        loadPort: bm.loadPort,
        dischargePort: bm.dischargePort,
        loadingTemperature: bm.loadingTemperature,
        voyageTemperature: bm.voyageTemperature,
        dischargeTemperature: bm.dischargeTemperature,
        adjacentTemperature: bm.adjacentTemperature,
        inertPressure: bm.inertPressure,
        hasNitrogen: bm.hasNitrogen,
        hasInhibitor: bm.hasInhibitor,
        comment: bm.comment,
        // Dedupe on ingest — production has corrupt configs with duplicate logTimes (AB#38024);
        // normalising here prevents the corruption from spreading through the UI and back into save payloads.
        logTimes: dedupeLogTimes(bm.logTimes),
        inputFields: mapCargoTemperatureInputFields(bm.inputFields),
        logTimesPerDay: bm.logTimesPerDay,
        oxygenUnitType: bm.oxygenUnitType,
        lastUpdatedUtc: bm.lastUpdatedUtc,
        lastUpdatedBy: bm.lastUpdatedBy,
        // Inhibitor questionnaire data
        inhibitorType: getInhibitorType(bm.inhibitorTypeId),
        inhibitorAmount: bm.inhibitorAmount,
        isOxygenDependent: bm.isOxygenDependent,
        minimumOxygenLevel: bm.minimumOxygenLevel,
        inhibitorAddedDate: bm.inhibitorAddedDate,
        inhibitorEffectivenessEntries: bm.inhibitorEffectivenessEntries,
        inhibitorExpiryAction: bm.inhibitorExpiryAction,
    };
};

export const mapCargoTemperatureConfigs = (bms: CargoTemperatureConfigBM[], offlineCargoTemperatureConfigs?: any[]): CargoTemperatureConfig[] => {
    if (!bms) return [];

    addOfflineDataToCargoTemperatureConfigs(bms, offlineCargoTemperatureConfigs ?? []);

    return bms.map(mapCargoTemperatureConfig);
};

export const mapCargoTemperatureConfigBM = (vm: CargoTemperatureConfig): CargoTemperatureConfigBM => {
    return {
        cargoTemperatureConfigId: vm.cargoTemperatureConfigId,
        imoNumber: vm.imoNumber,
        cargoNumber: vm.cargoNumber,
        tradeName: vm.tradeName,
        stowage: vm.stowage,
        loadPort: vm.loadPort,
        dischargePort: vm.dischargePort,
        loadingTemperature: vm.loadingTemperature,
        voyageTemperature: vm.voyageTemperature,
        dischargeTemperature: vm.dischargeTemperature,
        adjacentTemperature: vm.adjacentTemperature,
        inertPressure: vm.inertPressure,
        hasNitrogen: vm.hasNitrogen,
        hasInhibitor: vm.hasInhibitor,
        comment: vm.comment,
        // Dedupe logTimes so stale clients/SW caches with corrupt config data don't
        // re-send duplicate (ConfigId, Time) entries on save (AB#38024).
        logTimes: dedupeLogTimes(vm.logTimes),
        lastUpdatedUtc: vm.lastUpdatedUtc,
        lastUpdatedBy: vm.lastUpdatedBy,
        inputFields: mapCargoTemperatureInputFieldBMs(vm.inputFields),
        logTimesPerDay: vm.logTimesPerDay,
        oxygenUnitType: vm.oxygenUnitType,
        // Inhibitor questionnaire data
        inhibitorTypeId: getInhibitorTypeId(vm.inhibitorType),
        inhibitorAmount: vm.inhibitorAmount,
        isOxygenDependent: vm.isOxygenDependent,
        minimumOxygenLevel: vm.minimumOxygenLevel,
        inhibitorAddedDate: formatDateTime(vm.inhibitorAddedDate, 'yyyy-MM-dd'),
        inhibitorEffectivenessEntries: vm.inhibitorEffectivenessEntries,
        inhibitorExpiryAction: vm.inhibitorExpiryAction,
    };
};

export const mapCargoTemperatureConfigBMs = (vms: CargoTemperatureConfig[]): CargoTemperatureConfigBM[] =>
    vms ? vms.map(mapCargoTemperatureConfigBM) : [];

const addOfflineDataToCargoTemperatureConfigs = (bms: CargoTemperatureConfigBM[], offlineCargoTemperatureConfigs: any[]) => {
    offlineCargoTemperatureConfigs.forEach((offlineCargoTemperatureConfig) => {
        const saveCargoTemperatureConfigsRequest = offlineCargoTemperatureConfig.saveCargoTemperatureConfigsRequest;

        if (saveCargoTemperatureConfigsRequest?.response) {
            saveCargoTemperatureConfigsRequest.response.forEach((item) => {
                const indexOfBM = bms.findIndex((bm) => bm.cargoNumber === item.cargoNumber);

                if (indexOfBM > -1) {
                    bms[indexOfBM] = item;
                } else {
                    bms.push(item);
                }
            });
        }
    });
};
